import { useState, useEffect, useMemo } from "react";

import { RoadmapFeaturesQueryResult, getRoadmapFeatures } from "./client";
import { Cards } from "./components/cards.tsx";
import { ColumnHeaders } from "./components/column-headers.tsx";
import { Layout } from "./components/layout.tsx";
import { SearchWithFilters } from "./components/search-with-filters";
import {
    RoadmapFeaturesContextProvider,
    CLOUD_BASE_PATH,
    emptyFilters
} from "./context/context.tsx";
import { getQueryParamFromWindow, getSlugFromWindow } from "./context/url";
import {
    RoadmapFeatureFilters,
    SortCriteria,
    SelectedRoadmapFeature,
    SortKey,
    SortDirection,
    FilterOptionsByKey
} from "./types";

const pageBasePath = CLOUD_BASE_PATH;

const UniformRoadmapFeatures = () => {
    const { filters, sortCriteria, selectedFeature } = useMemo(() => {
        if (typeof window === "undefined") {
            return {
                filters: emptyFilters,
                sortCriteria: undefined,
                selectedFeature: undefined
            };
        }

        const filters: RoadmapFeatureFilters = {
            category: getQueryParamFromWindow("category"),
            search: getQueryParamFromWindow("search"),
            status: getQueryParamFromWindow("status"),
            selectedProduct: getQueryParamFromWindow("selectedProduct")
        };

        const sortCriteria: SortCriteria = {
            key: getQueryParamFromWindow("sortBy")[0] as SortKey,
            direction: getQueryParamFromWindow("sort")[0] as SortDirection
        };

        let selectedFeature: SelectedRoadmapFeature | undefined;
        const slug = getSlugFromWindow({ basePath: pageBasePath });
        if (slug) {
            selectedFeature = {
                pid: getQueryParamFromWindow("p")?.[0],
                slug
            };
        }

        return {
            filters,
            sortCriteria,
            selectedFeature
        };
    }, []);

    const { result, pending } = useQuery();
    if (pending) {
        return <p>Loading...</p>;
    }

    const features = result?.roadmapFeatureCollection.items ?? [];
    const filterOptionsByKey: FilterOptionsByKey = {
        category:
            result?.taxonomyRoadmapCategoryCollection.items.map((item) => ({
                value: item.queryParameter,
                label: item.roadmapCategoryNameExternal
            })) ?? [],
        selectedProduct:
            result?.taxonomyRoadmapAppCollection.items.map((item) => ({
                value: item.filterQueryParam,
                label: item.roadmapAppNameExternal
            })) ?? [],
        status:
            result?.taxonomyRoadmapStatusCollection.items.map((item) => ({
                value: item.roadmapStatusNameExternal,
                label: item.roadmapStatusNameExternal
            })) ?? []
    };

    return (
        <RoadmapFeaturesContextProvider
            pageBasePath={pageBasePath}
            filters={filters}
            sortCriteria={sortCriteria}
            selectedFeature={selectedFeature}
            features={features}
            filterOptionsByKey={filterOptionsByKey}
        >
            <Layout
                searchWithFilters={<SearchWithFilters />}
                columnHeaders={<ColumnHeaders />}
                cards={<Cards />}
            />
        </RoadmapFeaturesContextProvider>
    );
};

export default UniformRoadmapFeatures;

function useQuery() {
    const [result, setResult] = useState<
        RoadmapFeaturesQueryResult | undefined
    >(undefined);
    const [pending, setPending] = useState(false);

    useEffect(() => {
        setPending(true);
        getRoadmapFeatures({
            locale: "en-US",
            limit: 1000
        })
            .then((result) => {
                console.log("result", result);
                setResult(result);
            })
            .catch((err) => {
                console.log(err); // todo
            })
            .finally(() => {
                setPending(false);
                // todo
            });
    }, []);

    return {
        result,
        pending
    };
}
